import { BrowserSupport } from '@tableau/browser-support';
import * as _ from 'underscore';
import { setVizPath } from '../../app/actions/spaces/SetVizPathAction';
import { getAppStore } from '../../app/AppStore';
import { UrlParamKeys } from '../../app/UrlParamKeys';
import { FeatureFlags } from '../FeatureFlags';
import { getVizPath } from '../helpers/UrlHelpers';
import { DeviceType } from '../models/DeviceType';
import { IDataAlertInfo } from '../models/IDataAlertInfo';
import { IPreviewDeviceSize } from '../models/IPreviewDeviceSize';
import { ISize } from '../models/ISize';
import { IViz, VizType } from '../models/Viz';
import { Module } from '../Module';
import { DevicePreviewService } from '../../devicePreview/services/DevicePreviewService';
import { VizService, VizServiceEvents } from '../services/VizService';

export interface IVizScope extends ng.IScope {
  viz: IViz;
  dalert: IDataAlertInfo;  // this has to be lowercased, for some reason uppercased (dataAlert) does not work
  onVizSized: () => void;
  onVizInitialized: () => void;
}

const hasMaxCssValue = (maxCssValue: string) => (maxCssValue !== 'none');
const hasMinCssValue = (minCssValue: string) => (parseInt(minCssValue, 10) !== 0 && minCssValue !== 'auto');

let deviceTypeOfDataAlert: DeviceType;

// Usage: tb:viz(viz="viz"), where  $scope.viz is an IViz
// $scope.viz.path will be changed to reflect viz changes. You can $watch it
Module.instance().directive('tbViz', <any[]>[
  'VizService',
  '$location',
  '$timeout',
  '$window',
  '$rootScope',
  '$q',
  (VizService: VizService,
    $location: ng.ILocationService,
    $timeout: ng.ITimeoutService,
    $window: ng.IWindowService,
    $rootScope: ng.IRootScopeService,
    $q: ng.IQService) => {
    return {
      restrict: 'E',
      replace: true,
      templateUrl: 'viz.html',
      scope: {
        viz: '=',
        dalert: '=',
        onVizSized: '&',
        onVizInitialized: '&'
      },
      link: ($scope: IVizScope, elm: JQuery) => {
        const loadViz = () => {
          // Toolbar should always be on top in VizPortal
          const vizCreateOptions: tb.IVizCreateOptions = {
            toolbarPosition: tableau.ToolbarPosition.TOP,
            instanceIdToClone: $scope.viz.instanceId,
            // Note!!! When user reloads the webpage the navigation type is 'reload', but changes to 'navigate' by the time vizportal page is done displaying.
            // So need to capture the state before any navigations by script or framework.
            // This spot works for our vizportal usage of AngularJS.
            // See also comments in VizParameters.cs that sets navSrc.
            navType: window.performance.navigation.type.toString(),
            onFirstInteractive: () => {
              $scope.$apply(() => {
                $scope.viz.instanceId = viz.getInstanceId();
              });
              if ($scope.viz.vizType !== VizType.DeviceVizPreview) {
                $timeout(() => $rootScope.$emit(VizServiceEvents.VizLoadComplete));
              }
            }
          };

          // Include any custom viz parameters in the viz create options
          const params = _.extend(vizCreateOptions, $scope.viz.params);

          //If in edit data alert mode with nondefault device layout, ignore value of :device URL param
          //default isn't supported so we leave it blank if default
          if (deviceTypeOfDataAlert !== 'default') {
            //getDataAlerts returns the device type in Upper class, so we need to convert it to lowercase before
            //using it in the viz URL
            params[UrlParamKeys.DeviceType] = deviceTypeOfDataAlert.toString().toLowerCase();
          }

          params[':openAuthoringInTopWindow'] = true;
          params[':browserBackButtonUndo'] = true;
          params[':commentingEnabled'] = true;
          params[':reloadOnCustomViewSave'] = true;
          params[':showAppBanner'] = false;
          params[':isVizPortal'] = true;
          // TFSID: 610157
          const shareFlag = VizService.isShowShareParamOn();
          // Display the share options if the viz is a View type
          if ($scope.viz.vizType === VizType.View && shareFlag !== null) {
            params[':showShareOptions'] = shareFlag;
          }

          vizCreateOptions.width = '100%';
          vizCreateOptions.height = '100%';

          if ($scope.viz.vizType === VizType.DeviceVizPreview) {
            const size: IPreviewDeviceSize = DevicePreviewService.getSize();
            const device: DeviceType = DevicePreviewService.getPreviewDevice();

            const isDesktopSize: boolean = device === 'desktop';

            params[':commentingEnabled'] = false;
            params[':devicepreviewmode'] = true;
            params[':mobile'] = !isDesktopSize;
            params[UrlParamKeys.DeviceType] = device;

            if (FeatureFlags.SheetsAsTabsDashboardsPreviewLikePhoenix) {
              // TFS 852547 - Sheets as Tabs Dashboard should preview like Phoenix
              // Currently, there is a discrepancy between Mobile Web/Ackbar and Phoenix
              // When dashboards have sheets as tabs - Phoenix does not show the tabs.  Mobile Web/Ackbar does.
              // We are aligning with Phoenix on phone and tablet by removing the tabs.
              if (device === 'phone' || device === 'tablet') {
                params[':tabs'] = '0';
              }
            }

            vizCreateOptions.hideToolbar = true;
            vizCreateOptions.width = size.width;
            vizCreateOptions.height = size.height;

            // viz.refreshSize fills only the available size, which is minus the scrollbars.
            // Hiding the scrollbars at first to get the real available size.  Eventually, overflow-x -y are manually set
            // to scroll.
            // This line is here because if the size is set first, streaks may appear in Safari when the
            // browser is zoomed out. TFS840813
            // overflow-x and -y are set separately because IE and Edge require separate declarations to reset
            // each.
            elm.css('overflow-x', 'hidden');
            elm.css('overflow-y', 'hidden');

            elm.width(size.width);
            elm.height(size.height);

            // Reset padding
            elm.css('padding', '0');

            vizCreateOptions.onFirstVizSizeKnown = (evt: tb.IFirstVizSizeKnownEvent) => {
              const vizSize = evt.getVizSize();
              const sheetSize = vizSize.sheetSize;
              const scrollbarSize = BrowserSupport.ScrollbarWidth();

              const actualSizeAfterShrink: ISize = DevicePreviewService.getShrunkenSizeForPhone(sheetSize);
              if (actualSizeAfterShrink !== null) {
                viz.setFrameSize(actualSizeAfterShrink.width, actualSizeAfterShrink.height);
              } else {
                viz.refreshSize();
              }

              // Pushing scrollbars outside the preview box to increase viewable space - TFS837062
              // Also fixes the problem IE has in using the clientWidth for rendering the horizontal scrollbar - TFS846457
              // overflow is set manually on both axes to control the incidental y-scroll that appears when padding is added as a
              // result of the x-scroll and vice versa
              const containerElement = elm.get(0);
              const iFrameElement = containerElement.getElementsByTagName('iframe')[0];

              const shouldHaveVerticalScrollbar: boolean = containerElement.clientHeight < iFrameElement.offsetHeight;
              const shouldHaveHorizontalScrollbar: boolean = containerElement.clientWidth < iFrameElement.offsetWidth;

              if (shouldHaveVerticalScrollbar) {
                elm.css('paddingRight', scrollbarSize);
                elm.css('overflow-y', 'scroll');
              }
              if (shouldHaveHorizontalScrollbar) {
                elm.css('paddingBottom', scrollbarSize);
                elm.css('overflow-x', 'scroll');
              }
            };
          }

          $scope.onVizInitialized();

          const uri = VizService.getUri($scope.viz);
          const viz = new tableau.Viz(elm.get(0), uri, params);

          // Ensure we remove the viz when navigating away
          $scope.$on('$destroy', () => viz.dispose());

          // note: The event handlers below are to update the url from changes in the viz, such as tab
          // switches or custom view loads. Ideally the JS API would tell us when the url changes in a
          // URL_CHANGED event, but we didn't have enough time to do that for 9.0.
          // warning: the custom view events fire at very strange times, for example see BUGZID: 132554
          const urlChangeEvents = [
            tableau.TableauEventName.CUSTOM_VIEW_LOAD,
            tableau.TableauEventName.CUSTOM_VIEW_REMOVE,
            tableau.TableauEventName.CUSTOM_VIEW_SAVE,
            tableau.TableauEventName.TAB_SWITCH
          ];

          _.each(urlChangeEvents, (eventName: tb.TableauEventNameEnum) => {
            viz.addEventListener(eventName, (e: tb.ICustomViewEvent) => {
              viz.getCurrentUrlAsync().then((url: string) => {
                $scope.$apply(() => {
                  $scope.viz.instanceId = viz.getInstanceId();
                  $scope.viz.path = decodeURIComponent(getVizPath(url));
                  $scope.viz.isExplicitOriginalViz = VizService.getIsExplicitOriginalVizFromUrl(url);

                  // when changing path via tabs, update the app state which is used for the Provider
                  // to update the url (see AdminAnalysisVizSpaceProvider for an example)
                  // Do NOT dispatch this for device preview vizzes, because those are not controlled
                  // by React Router and we do not want those to update the url
                  if ($scope.viz.vizType !== VizType.DeviceVizPreview) {
                    getAppStore().dispatch(setVizPath($scope.viz.path));
                  }
                });
              });
            });
          });

          /// TFSID: 629994 Receive a postMessage event from vizclient iframe to remove :editDataAlert URL parameter
          /// This is a undocumented, private event that we're not exposing publicly via the JS API client library.
          /// Thus, we need to use our own postMessage event handler instead of using viz.addEventListener
          /// See https://mytableau.tableaucorp.com/x/4PijBg for details
          $window.addEventListener('message', (event: any) => {
            if (event.data != null && typeof(event.data) === 'string'
              && (event.data.indexOf('RemoveEditDataAlertUrlParamEvent') > 0)) {
              delete $scope.viz.params[UrlParamKeys.EditDataAlert];
              delete $scope.viz.params[UrlParamKeys.ResumeDataAlert];
              VizService.removeEditDataAlertUrlParam($scope);
            }

          });

          if ($scope.viz.allowReloadViz) {
            $scope.viz.reloadViz = () => {
              viz.dispose();
              loadViz();
            };
          }
        };

        const applyWatchThenLoadViz = () => {
          // Wait to load the viz until the container is sized.  We use the container size to determine device type
          // for DSD, and if the container is sized (e.g. tb-top hasn't been evaluated on the ui-view container)
          // then we will load the wrong view.
          // TFSID: 519186 - Race condition in vizportal shows phone layout
          const unbindWatch = $scope.$watch(
            () => elm.width() * elm.height(),
            (newValue: number) => {
              if (newValue > 0) {
                unbindWatch();
                loadViz();
              }
            }
          );
        };

        deviceTypeOfDataAlert = $scope.dalert?.deviceType ?? 'default';
        applyWatchThenLoadViz();
      }
    };
  }
]);
